import { z } from "zod";
import { isRegisteredIntegrationProvider } from "./integrations";

/**
 * Lifecycle states of a connection, shared by hub and console.
 * The console shows the same state the hub holds: pending (awaiting
 * approval or OAuth), active, degraded, failing, expired, revoked, blocked.
 */
export const connectionStatuses = [
  "pending",
  "active",
  "degraded",
  "failing",
  "expired",
  "revoked",
  "blocked",
] as const;
export const connectionStatusSchema = z.enum(connectionStatuses);
export type ConnectionStatus = z.infer<typeof connectionStatusSchema>;

/**
 * The scope that owns a connection, mirroring the hub. A connection belongs to
 * either one project or one organisation: a GitHub App installation, a Slack
 * workspace, or an SSO provider is naturally organisation-level.
 */
export const connectionScopeSchema = z.discriminatedUnion("level", [
  z.object({ level: z.literal("project"), id: z.string().uuid() }),
  z.object({ level: z.literal("organisation"), id: z.string().uuid() }),
]);
export type ConnectionScope = z.infer<typeof connectionScopeSchema>;

/**
 * One configured connection, as the console proxy returns it. Values are
 * references only, never secrets: credentialRef names the reference, and no
 * endpoint returns the value.
 *
 * projectId is the project the console is showing the connection in. It is
 * null for a connection owned by an organisation and listed through an
 * organisation, and set for a project connection and for an organisation
 * connection shared into the project. scope names the owner.
 */
export const connectionSchema = z.object({
  id: z.string().min(1),
  projectId: z.string().uuid().nullable(),
  scope: connectionScopeSchema,
  /**
   * The connection is owned by an organisation and reached here through a
   * grant to the project. A granted connection is usable in this project and
   * no other.
   */
  granted: z.boolean(),
  integrationType: z.string().refine((p) => isRegisteredIntegrationProvider(p), {
    message: "Integration type must be a registered integration provider",
  }),
  name: z.string().min(1),
  status: connectionStatusSchema,
  authMethod: z.string().min(1),
  credentialRef: z.string().nullable(),
  scopes: z.array(z.string()),
  health: z.enum(["healthy", "degraded", "unreachable", "unknown"]),
  healthCheckedAt: z.string().datetime().nullable(),
  lastUsedAt: z.string().datetime().nullable(),
  expiresAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Connection = z.infer<typeof connectionSchema>;

export const connectionListSchema = z.array(connectionSchema);
export type ConnectionList = z.infer<typeof connectionListSchema>;

/** `POST /api/v1/projects/:id/connections`. Idempotency key reuses the row on retry. */
export const createConnectionSchema = z
  .object({
    integrationType: z
      .string()
      .trim()
      .min(1)
      .max(120)
      .refine((val) => isRegisteredIntegrationProvider(val), {
        message: "Integration type must be a registered integration provider",
      }),
    name: z.string().trim().min(1).max(200),
    authMethod: z.string().trim().min(1).max(120),
    credentialRef: z.string().trim().min(1).max(200).nullable().optional(),
    scopes: z.array(z.string().trim().min(1).max(120)).default([]),
    expiresAt: z.string().datetime().nullable().optional(),
    idempotencyKey: z.string().trim().min(1).max(120),
  })
  .strict();
export type CreateConnection = z.infer<typeof createConnectionSchema>;

/** `POST /api/v1/projects/:id/connections/:connectionId/rotate`. */
export const rotateConnectionSchema = z
  .object({
    credentialRef: z.string().trim().min(1).max(200),
  })
  .strict();
export type RotateConnection = z.infer<typeof rotateConnectionSchema>;

/** `PATCH /api/v1/projects/:id/connections/:connectionId/scopes`. */
export const updateConnectionScopesSchema = z
  .object({
    scopes: z.array(z.string().trim().min(1).max(120)).min(1),
  })
  .strict();
export type UpdateConnectionScopes = z.infer<typeof updateConnectionScopesSchema>;

/**
 * `POST /api/v1/projects/:id/connections/:connectionId/tools`. The caller names the tool and the
 * granted scopes it may use. Write scopes need `allowWrite`, which is false by default. Risk and
 * access are never sent: the hub derives them from the scopes.
 */
export const attachConnectionToolSchema = z
  .object({
    tool: z.string().trim().min(1).max(160),
    scopes: z.array(z.string().trim().min(1).max(120)).min(1).max(20),
    allowWrite: z.boolean().default(false),
  })
  .strict();
export type AttachConnectionTool = z.infer<typeof attachConnectionToolSchema>;

/**
 * One audit record about a connection, as the console Activity view shows it. The actor is a kind,
 * not an identifier: "member" for a person, "system" for the platform's own checks. Object
 * references and evidence digests stay on the hub.
 */
export const connectionActivityEntrySchema = z.object({
  id: z.string().min(1),
  action: z.string().min(1),
  actorKind: z.enum(["member", "system"]),
  occurredAt: z.string().datetime(),
});
export type ConnectionActivityEntry = z.infer<typeof connectionActivityEntrySchema>;

/** One page of activity entries, oldest first. The page size is at most 200, the hub's largest page. */
export const connectionActivityListSchema = z.array(connectionActivityEntrySchema).max(200);
export type ConnectionActivityList = z.infer<typeof connectionActivityListSchema>;

/**
 * `GET /api/v1/projects/:id/connections/:connectionId/activity?limit=&cursor=`. A null nextCursor
 * means this is the last page. Pass nextCursor back as `cursor` to read the next, older page.
 */
export const connectionActivityPageSchema = z.object({
  activity: connectionActivityListSchema,
  nextCursor: z.string().min(1).max(1000).nullable(),
});
export type ConnectionActivityPage = z.infer<typeof connectionActivityPageSchema>;
